import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import { css, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { fromEvent, merge, timer } from 'rxjs'
import { filter, map, scan, takeUntil, tap } from 'rxjs/operators'
import '@schmancy/primitives/visually-hidden/visually-hidden'

export type SchmancyTypographyFieldChangeEvent = CustomEvent<{ value: string }>
export type SchmancyTypographyFieldInputEvent = CustomEvent<{ value: string }>
export type SchmancyTypographyFieldEnterEvent = CustomEvent<{ value: string }>
export type SchmancyTypographyFieldRevertEvent = CustomEvent<{ value: string }>

/**
 * `plaintext-only` is what keeps a paste from carrying the source's own
 * `font-family`/`font-size`/`color` into a field whose entire API is the type
 * scale. An engine that does not know the keyword treats it as an invalid
 * enumerated value and falls back to inheriting `false`, which would leave the
 * field silently uneditable — so it is probed once instead of assumed.
 */
const EDITING_MODE: 'plaintext-only' | 'true' = (() => {
	const probe = document.createElement('div')
	try {
		probe.contentEditable = 'plaintext-only'
	} catch {
		return 'true'
	}
	return probe.contentEditable === 'plaintext-only' ? 'plaintext-only' : 'true'
})()

/**
 * Form-associated editable typography. Preserves the `<schmancy-typography>`
 * type-scale APIs (`type`, `token`, `align`, `weight`, `transform`, `maxLines`)
 * but participates in `<schmancy-form>` as a single-string field via
 * `ElementInternals`.
 *
 * The tab stop is the inner editable's own `tabindex="0"`, never the host's: a
 * host `tabindex` delegates focus inward to an element a focus trap's list does
 * not hold, so `indexOf(activeElement)` misses and every Tab resets to the first
 * stop — the operator reaches the field and can never leave it.
 *
 * Escape takes the key only when there is an edit to undo, the way a native
 * search input clears before it lets Escape through, so an untouched field
 * leaves the key to the overlay it sits in.
 *
 * @element schmancy-typography-field
 * @fires input - `CustomEvent<{ value: string }>` on every keystroke (mirrors
 *   `schmancy-input`'s live `input` event) — the value a consumer's own host
 *   state should track live, since `change` only settles on commit.
 * @fires change - `CustomEvent<{ value: string }>` on commit (blur, or Enter when
 *   `multiline` is false), only when the committed text differs from the value
 *   at focus — tabbing through never fires it.
 * @fires revert - `CustomEvent<{ value: string }>` when Escape restores the last
 *   value the field held while valid. Fires in place of `change`, so a consumer
 *   that refused the replaced text can drop its refusal.
 * @fires enter - `CustomEvent<{ value: string }>` (cancelable) when Enter is pressed
 *   and `multiline` is false. Default action is commit-via-blur; `preventDefault()`
 *   it to keep focus and drive custom Enter behaviour (e.g. a todo list advancing
 *   to the next line). With `multiline` true, Enter inserts a native newline instead
 *   — this event never fires and the field never blurs on Enter.
 */
@customElement('schmancy-typography-field')
export class SchmancyTypographyField extends HostAriaLabelMixin(SchmancyFormField(css`
	/* The state fill spreads 4px past the host on the same corner, so hover and
	   focus mark the value without moving a glyph. Transparent at rest, so the
	   resting field paints nothing and only the colour animates. */
	:host {
		display: block;
		font-family: inherit;
		hyphens: none;
		cursor: text;
		border-radius: var(--schmancy-radius-xs, 0.4375rem); /* mirrors --radius-xs (rounded-xs) */
		--_state-layer: transparent;
		background-color: var(--_state-layer);
		box-shadow: 0 0 0 4px var(--_state-layer);
		transition:
			background-color 150ms,
			box-shadow 150ms;
		min-height: 1em;
	}

	:host(:hover:not([readonly]):not([disabled]):not(:focus-within)) {
		--_state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 8%, transparent);
	}

	:host(:focus-within:not([readonly])) {
		--_state-layer: color-mix(in srgb, var(--schmancy-sys-color-surface-on) 12%, transparent);
	}

	:host([disabled]) {
		opacity: 0.5;
		cursor: not-allowed;
	}

	:host([readonly]) {
		cursor: default;
	}

	:host([align='center']) {
		text-align: center;
	}
	:host([align='left']) {
		text-align: start;
	}
	:host([align='right']) {
		text-align: right;
	}
	:host([align='justify']) {
		text-align: justify;
	}

	:host([weight='bold']) {
		font-weight: 700;
	}
	:host([weight='medium']) {
		font-weight: 500;
	}
	:host([weight='normal']) {
		font-weight: 400;
	}

	:host([transform='uppercase']) {
		text-transform: uppercase;
	}
	:host([transform='lowercase']) {
		text-transform: lowercase;
	}
	:host([transform='capitalize']) {
		text-transform: capitalize;
	}
	:host([transform='normal']) {
		text-transform: none;
	}

	:host([type='display']),
	:host([type='headline']),
	:host([type='body']) {
		font-weight: 400;
	}
	:host([type='label']),
	:host([type='subtitle']),
	:host([type='title']) {
		font-weight: 500;
	}

	:host([type='display'][token='xl']) {
		font-size: 72px;
		line-height: 80px;
		font-weight: 400;
	}
	:host([type='display'][token='lg']) {
		font-size: 57px;
		line-height: 64px;
		font-weight: 400;
	}
	:host([type='display'][token='md']) {
		font-size: 45px;
		line-height: 52px;
		font-weight: 400;
	}
	:host([type='display'][token='sm']) {
		font-size: 36px;
		line-height: 44px;
		font-weight: 400;
	}
	:host([type='display'][token='xs']) {
		font-size: 28px;
		line-height: 36px;
		font-weight: 400;
	}

	:host([type='headline'][token='xl']) {
		font-size: 36px;
		line-height: 44px;
		font-weight: 400;
	}
	:host([type='headline'][token='lg']) {
		font-size: 32px;
		line-height: 40px;
		font-weight: 400;
	}
	:host([type='headline'][token='md']) {
		font-size: 28px;
		line-height: 36px;
		font-weight: 400;
	}
	:host([type='headline'][token='sm']) {
		font-size: 24px;
		line-height: 32px;
		font-weight: 400;
	}
	:host([type='headline'][token='xs']) {
		font-size: 20px;
		line-height: 28px;
		font-weight: 400;
	}

	:host([type='title'][token='xl']) {
		font-size: 24px;
		line-height: 32px;
		font-weight: 400;
	}
	:host([type='title'][token='lg']) {
		font-size: 22px;
		line-height: 28px;
		font-weight: 400;
	}
	:host([type='title'][token='md']) {
		font-size: 16px;
		line-height: 24px;
		font-weight: 500;
	}
	:host([type='title'][token='sm']) {
		font-size: 14px;
		line-height: 20px;
		font-weight: 500;
	}
	:host([type='title'][token='xs']) {
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
	}

	:host([type='subtitle'][token='xl']) {
		font-size: 20px;
		line-height: 28px;
		font-weight: 500;
	}
	:host([type='subtitle'][token='lg']) {
		font-size: 18px;
		line-height: 24px;
		font-weight: 500;
	}
	:host([type='subtitle'][token='md']) {
		font-size: 16px;
		line-height: 24px;
		font-weight: 500;
	}
	:host([type='subtitle'][token='sm']) {
		font-size: 14px;
		line-height: 20px;
		font-weight: 500;
	}
	:host([type='subtitle'][token='xs']) {
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
	}

	:host([type='body'][token='xl']) {
		font-size: 18px;
		line-height: 28px;
		font-weight: 400;
	}
	:host([type='body'][token='lg']) {
		font-size: 16px;
		line-height: 24px;
		font-weight: 400;
	}
	:host([type='body'][token='md']) {
		font-size: 14px;
		line-height: 20px;
		font-weight: 400;
	}
	:host([type='body'][token='sm']) {
		font-size: 12px;
		line-height: 16px;
		font-weight: 400;
	}
	:host([type='body'][token='xs']) {
		font-size: 10px;
		line-height: 14px;
		font-weight: 400;
	}

	:host([type='label'][token='xl']) {
		font-size: 16px;
		line-height: 22px;
		font-weight: 500;
	}
	:host([type='label'][token='lg']) {
		font-size: 14px;
		line-height: 20px;
		font-weight: 500;
	}
	:host([type='label'][token='md']) {
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
	}
	:host([type='label'][token='sm']) {
		font-size: 11px;
		line-height: 16px;
		font-weight: 500;
	}
	:host([type='label'][token='xs']) {
		font-size: 10px;
		line-height: 14px;
		font-weight: 500;
	}

	.edit {
		min-height: 1em;
		font: inherit;
		color: inherit;
		letter-spacing: inherit;
		line-height: inherit;
		border-radius: var(--schmancy-radius-xs, 0.4375rem); /* mirrors --radius-xs (rounded-xs) */
	}
	.edit:empty::before {
		content: attr(data-placeholder);
		pointer-events: none;
		display: block;
		opacity: 0.35;
	}

	:host([multiline]) .edit {
		white-space: pre-wrap;
	}
`)) {
	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	}

	@property({ type: String, reflect: true })
	type: 'display' | 'headline' | 'title' | 'subtitle' | 'body' | 'label' = 'body'

	/**
	 * @deprecated Prefer Tailwind responsive text classes; set `token=""` and use
	 * `class="text-sm md:text-base lg:text-lg"`.
	 */
	@property({ type: String, reflect: true })
	token: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '' = 'md'

	@property({ type: String, reflect: true })
	align: 'left' | 'center' | 'justify' | 'right' | undefined

	@property({ type: String, reflect: true })
	weight: 'normal' | 'medium' | 'bold' | undefined

	@property({ type: String, reflect: true })
	transform: 'uppercase' | 'lowercase' | 'capitalize' | 'normal' | undefined

	@property({ type: Number })
	maxLines: 1 | 2 | 3 | 4 | 5 | 6 | undefined

	@property({ type: String }) placeholder = ''

	/**
	 * Read after the field's name and never shown — the state a consumer
	 * announces with the value, such as "changed", without renaming the control.
	 */
	@property({ type: String }) description = ''

	/**
	 * When true, Enter inserts a real newline instead of committing the field,
	 * the commit-time collapse (`focusout`) preserves internal line breaks
	 * instead of squashing them to a single space, and `.edit` renders with
	 * `white-space: pre-wrap` so those breaks are visible rather than
	 * collapsing back down by the block default.
	 */
	@property({ type: Boolean, reflect: true }) multiline = false

	/** Mirrors `<schmancy-input>`'s prop — the keyboard a touch device offers for this field's data. */
	@property({ type: String })
	inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'

	/** Narrowed from the mixin's wide union. Mirrored to the `value` attribute so SSR / initial-render markup survives. */
	@property({ type: String, reflect: true })
	override value: string = ''

	@property({ type: Boolean })
	public autofocus = false

	private editRef = createRef<HTMLDivElement>()

	/**
	 * The value at the start of the current edit session — captured on
	 * `focusin`, compared against on `focusout` to decide whether `change`
	 * fires. Needed because `input` now live-syncs `this.value` on every
	 * keystroke (mirrors `schmancy-input`): comparing the commit-time value
	 * against `this.value` itself would always read equal (the last
	 * keystroke already wrote it) and `change` would never fire. A native
	 * `<input>` gets this delta for free from the browser's own `change`
	 * event, which fires only when content differs from its value at focus.
	 */
	private valueAtFocus = ''

	/**
	 * What Escape restores: the value the field last held while valid. A commit
	 * the consumer refused through `setCustomValidity` leaves the refused text in
	 * an invalid field, so a later focus keeps this where it was instead of
	 * taking the refused text as the edit's starting point.
	 */
	private revertValue = ''

	selectAll(): void {
		const el = this.editRef.value
		if (!el) return
		el.focus()
		const sel = window.getSelection()
		if (sel && el.textContent) {
			const range = document.createRange()
			range.selectNodeContents(el)
			sel.removeAllRanges()
			sel.addRange(range)
		}
	}

	focusEnd(): void {
		const el = this.editRef.value
		if (!el) return
		el.focus()
		const sel = window.getSelection()
		if (!sel) return
		const range = document.createRange()
		range.selectNodeContents(el)
		range.collapse(false)
		sel.removeAllRanges()
		sel.addRange(range)
	}

	/**
	 * The text as a commit leaves it. A paste is the one input this field cannot
	 * intercept key by key, and it arrives multi-line: a raw newline under
	 * `plaintext-only`, a `<div>`-per-line tree under the `true` fallback.
	 * `aria-multiline="false"` is a hard claim a consumer's `whitespace-nowrap`
	 * cannot keep, so a single-line field spends each break as one space; a
	 * multiline field's breaks are content.
	 */
	private committedText(text: string): string {
		return this.multiline ? text.trim() : text.replace(/\s*\n+\s*/g, ' ').trim()
	}

	/**
	 * The characters in the editable, with its line breaks. `innerText` is the
	 * one read that turns either DOM shape a break leaves (a raw newline, a
	 * `<div>` or `<br>`) into `\n`, but it returns the rendered text, so an
	 * inherited `text-transform: uppercase` would store "HALLEN SIEBEN" for
	 * "Hallen Sieben". The transform is lifted for the read and nothing paints
	 * in between; the caret is untouched, since no node changes.
	 */
	private typedText(): string {
		const el = this.editRef.value
		if (!el) return ''
		el.style.textTransform = 'none'
		const text = el.innerText
		el.style.removeProperty('text-transform')
		return text
	}

	protected override firstUpdated(changedProps: PropertyValues): void {
		super.firstUpdated(changedProps)

		// firstUpdated only — never re-run on reconnect, or a menu-slotted field
		// would steal focus back every time the menu reopens.
		if (this.autofocus) {
			timer(0)
				.pipe(takeUntil(this.disconnecting))
				.subscribe(() => this.focusEnd())
		}

		this.valueAtFocus = this.value
		this.revertValue = this.value
		this.wireEditableInputEvents()
	}

	/**
	 * Bound to the inner editable (`this.editRef.value`), never to `this` —
	 * the host also dispatches its own custom `input` event from inside this
	 * same handler, and a listener on `this` would re-catch that dispatch
	 * (same event name, same node), running itself twice per keystroke and
	 * self-cancelling via its own `stopPropagation()` on the second pass.
	 * Mirrors `schmancy-input`'s `setupInputEvents()`, which binds to
	 * `this.inputElement` for the identical reason.
	 *
	 * Called from `firstUpdated` (shadow DOM ready) and again from
	 * `connectedCallback` on reconnect (see the `if (this.hasUpdated)` guard
	 * there) — `firstUpdated` only ever runs once per element lifetime, and
	 * the per-connection `disconnecting` this pipeline rides tore the prior
	 * connection's subscription down on move-out.
	 */
	private wireEditableInputEvents(): void {
		const el = this.editRef.value
		if (!el) return

		fromEvent<InputEvent>(el, 'input')
			.pipe(
				filter(() => !this.disabled && !this.readonly),
				tap(ev => {
					// Stop the native event — dispatch our own custom `input`
					// instead, from the host, so a consumer binding `@input` gets
					// a live per-keystroke value rather than one gated on
					// blur/Enter like `change`.
					ev.stopPropagation()

					const typed = this.typedText()
					if (!typed.trim()) el.textContent = ''

					const value = typed.trim() ? typed : ''
					if (value === this.value) return
					this.value = value
					this.dispatchEvent(
						new CustomEvent<{ value: string }>('input', {
							detail: { value },
							bubbles: true,
							composed: true,
						}),
					)
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	override connectedCallback(): void {
		super.connectedCallback()

		// Re-wire on RECONNECT: firstUpdated ran once and the per-connection
		// `disconnecting` tore down the prior connection's sub on the move-out;
		// `hasUpdated` is false before the first render (firstUpdated owns that),
		// true on every reparent (e.g. a menu-slotted field moved into an
		// overlay on open).
		if (this.hasUpdated) this.wireEditableInputEvents()

		merge(
			fromEvent(this, 'pointerdown').pipe(map(() => 'pointer')),
			fromEvent(document, 'keydown', { capture: true }).pipe(map(() => 'key')),
			fromEvent(this, 'focusin').pipe(map(() => 'focus')),
			// Read as the window blurs, while focus still says whether this field held
			// it: a selection inside nested shadow roots is reported against the
			// outermost host, so a later read cannot tell a return from an arrival.
			fromEvent(window, 'blur').pipe(
				map(() => (this.shadowRoot?.activeElement === this.editRef.value ? 'away-from-here' : 'away')),
			),
		)
			.pipe(
				// Which input came just before each focus. The focus a pointerdown causes
				// spends it, and so does the next key, so a press that never focused the
				// field cannot cost a later Tab its caret.
				scan((last, input) => ({ input, previous: last.input }), { input: 'key', previous: 'key' }),
				filter(({ input }) => input === 'focus' && !this.disabled && !this.readonly),
				tap(() => {
					this.valueAtFocus = this.value
					this.revertValue = this.internals?.validity.valid === false ? this.revertValue : this.value
				}),
				// A pointer puts the caret where it lands, and a return to the window this
				// field held focus in finds it where it was left. Focus Tabbed in from
				// another frame, which held focus as the window blurred, carries no point,
				// like keyboard and programmatic focus, and editing a value usually means
				// continuing it, so the caret goes to the end.
				filter(({ previous }) => previous !== 'pointer' && previous !== 'away-from-here'),
				tap(() => this.focusEnd()),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		fromEvent<FocusEvent>(this, 'focusout')
			.pipe(
				filter(() => !this.disabled && !this.readonly),
				tap(() => {
					this.markTouched()
					const el = this.editRef.value
					if (!el) return
					const typed = this.typedText()
					const newValue = this.committedText(typed)
					// Compared in committed form against the value at focus, not against
					// `this.value`, which the live `input` handler already overwrote — so a
					// pass that changed nothing, tabbing through a value not yet in committed
					// form included, leaves the value as it was and fires nothing.
					if (newValue === this.committedText(this.valueAtFocus)) {
						this.value = this.valueAtFocus
						return
					}
					// Rewritten on commit, where the caret is already gone and changing the
					// rendered text disturbs nothing.
					if (!newValue) el.textContent = ''
					else if (typed !== newValue) el.innerText = newValue
					if (newValue !== this.value) this.value = newValue
					this.emitChange({ value: newValue })
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()

		const keydown$ = fromEvent<KeyboardEvent>(this, 'keydown').pipe(filter(() => !this.disabled && !this.readonly))

		merge(
			keydown$.pipe(
				filter(e => e.key === 'Enter' && !this.multiline),
				tap(e => {
					e.preventDefault()
					const proceed = this.dispatchEvent(
						new CustomEvent('enter', {
							detail: { value: this.typedText() },
							bubbles: true,
							composed: true,
							cancelable: true,
						}),
					)
					if (proceed) (this.editRef.value ?? this).blur()
				}),
			),
			keydown$.pipe(
				filter(e => e.key === 'Escape' && !e.isComposing),
				filter(
					() => this.committedText(this.typedText()) !== this.committedText(this.revertValue),
				),
				tap(e => {
					// An Escape that undoes an edit is spent on it: no enclosing surface may
					// also read it as a dismiss, and nothing native may act on it.
					e.preventDefault()
					e.stopPropagation()
					const el = this.editRef.value
					if (el) el.textContent = this.revertValue
					this.value = this.revertValue
					// Restoring is not a commit: the restored value becomes the edit's
					// baseline, so leaving the field fires no `change`.
					this.valueAtFocus = this.revertValue
					// `revert` precedes the restoring `input`, so a consumer reacting to
					// either one has seen the revert first.
					this.dispatchEvent(
						new CustomEvent<{ value: string }>('revert', {
							detail: { value: this.revertValue },
							bubbles: true,
							composed: true,
						}),
					)
					this.dispatchEvent(
						new CustomEvent<{ value: string }>('input', {
							detail: { value: this.revertValue },
							bubbles: true,
							composed: true,
						}),
					)
					;(el ?? this).blur()
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	protected override updated(changedProperties: PropertyValues): void {
		super.updated(changedProperties)

		if (changedProperties.has('maxLines')) {
			this.classList.remove(
				'line-clamp-1',
				'line-clamp-2',
				'line-clamp-3',
				'line-clamp-4',
				'line-clamp-5',
				'line-clamp-6',
			)
			if (this.maxLines) this.classList.add(`line-clamp-${this.maxLines}`)
		}

		// Sync DOM text only when the editable isn't the focused element — typing
		// already produces the new text; rewriting it would collapse the caret.
		// `document.activeElement` stops at the shadow host, so compare against
		// `this.shadowRoot.activeElement` to actually detect inner-element focus.
		if (changedProperties.has('value')) {
			const el = this.editRef.value
			const focusedInside = this.shadowRoot?.activeElement === el
			if (el && !focusedInside) {
				if (this.value) el.innerText = String(this.value)
				else el.textContent = ''
			}
		}
	}

	protected render(): unknown {
		const editable = !this.disabled && !this.readonly
		const invalid = this.error && !!this.validationMessage
		return html`
			<div
				${ref(this.editRef)}
				class=${this.classMap({
					edit: true,
					'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-4': true,
					'focus-visible:outline-primary-default': !this.error,
					'focus-visible:outline-error-default': this.error,
					'outline outline-2 outline-offset-2 outline-error-default': this.error,
				})}
				contenteditable=${editable ? EDITING_MODE : 'false'}
				tabindex=${ifDefined(this.disabled ? undefined : '0')}
				inputmode=${ifDefined(this.inputmode)}
				role="textbox"
				aria-multiline=${this.multiline ? 'true' : 'false'}
				aria-required=${this.required ? 'true' : 'false'}
				aria-invalid=${this.error ? 'true' : 'false'}
				aria-readonly=${this.readonly ? 'true' : 'false'}
				aria-label=${this.ariaLabel || this.label || this.placeholder || nothing}
				aria-placeholder=${this.placeholder || nothing}
				aria-describedby=${[this.description && 'description', (invalid || this.hint) && 'supporting-text']
					.filter(Boolean)
					.join(' ') || nothing}
				data-placeholder=${this.placeholder ?? ''}
			></div>
			<schmancy-visually-hidden id="description">${this.description}</schmancy-visually-hidden>
			${
				invalid
					? html`<div id="supporting-text" role="alert" class="text-error-default text-xs mt-1 wrap-anywhere">
							${this.validationMessage}
						</div>`
					: this.hint
						? html`<div id="supporting-text" class="text-surface-onVariant text-xs mt-1 wrap-anywhere">${this.hint}</div>`
						: nothing
			}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-typography-field': SchmancyTypographyField
	}
}
